'use client';

import type { Layout } from '@rive-app/react-canvas';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';
import { loadRiveAnimation } from '@/assets/animations/rive/rive';

const RiveAnimationContent = dynamic(
  () => import('@/components/atoms/rive-animation/rive-animation-content'),
  {
    ssr: false,
  },
);

export interface RiveAnimationProps {
  /** Whether the animation should start automatically */
  autoplay?: boolean;
  /** Duration in milliseconds to play before pausing */
  duration?: number;
  /** The height of the animation */
  height?: number;
  /** Whether the animation is hovered */
  isHovered?: boolean;
  /** The name of the state machine input to use for hover state. Defaults to 'isHovered' */
  isHoveredInputName?: string;
  /** Layout configuration for alignment and fit */
  layout?: Layout;
  /** The name of the Rive animation file to load */
  name: RiveAnimationName;
  /** Trigger to play/pause the animation externally */
  play?: boolean;
  /** The state machine(s) to use from the Rive file */
  stateMachines?: string | string[];
  /** The width of the animation */
  width?: number;
}

const RiveAnimation = ({ name, ...props }: RiveAnimationProps) => {
  const [src, setSrc] = useState<string>();

  useEffect(() => {
    let terminate = false;

    loadRiveAnimation(name).then(src => {
      if (terminate) return;

      setSrc(src);
    });

    return () => {
      terminate = true;
    };
  }, [name]);

  if (!src) {
    return null;
  }

  return <RiveAnimationContent {...props} src={src} />;
};

RiveAnimation.displayName = 'RiveAnimation';

export default RiveAnimation;
